<template>
    <PageWrapper>
        <template #headline>
            <fa icon="fa-store" />
            {{ $t('platform') }}:
            <PlatformTitle
                v-if="platform"
                :platform="platform"
            />
        </template>

        <template #actions>
            <BackButton
                v-if="$router.options.history.state.back"
                @click="router.go(-1)"
            />
            <BackButton
                v-if="$router.options.history.state.back !== '/platforms'"
                icon="fa-list"
                title="platforms"
                @click="toPlatforms"
            />
            <EditButton
                v-if="userStore.can('update_platform')"
                @click="editPlatform"
            />
            <CustomizePlatformButton
                v-if="userStore.can('update_platform')"
                @click="customizePlatform"
            />
            <DeleteButton
                v-if="userStore.can('delete_platform')"
                @click="openDeletePlatformModal"
            />
            <ActionsMenu v-if="userStore.can('update_platform')" />
        </template>
        <!-- SUMMARY -->
        <SummaryWrapper>
            <PlatformSummary v-if="platform.platform_uid" />

            <template #sidebar>
                <PaymentAccountSummary
                    v-if="platform.payment_account !== null"
                />
            </template>
        </SummaryWrapper>

        <PlatformEmailTemplatesTable v-if="platform.platform_uid">
            <template #actions>
                <DeleteBulkButton
                    v-if="userStore.can('update_platform')"
                    v-model:selected="selectedPlatformEmailTemplatesIds"
                    @click.stop="() => handleDeletePlatformEmailTemplates()"
                />
                <CreateButton
                    v-if="userStore.can('update_platform')"
                    aria-controls="platform-email-templates-modals"
                    @click.stop="
                        () => handleEditOrCreatePlatformEmailTemplate()
                    "
                />
            </template>
        </PlatformEmailTemplatesTable>
        <PlatformDisclaimersTable v-if="platform.platform_uid">
            <template #actions>
                <DeleteBulkButton
                    v-if="userStore.can('update_platform')"
                    v-model:selected="selectedPlatformDisclaimersIds"
                    @click.stop="() => handleDeletePlatformDisclaimers()"
                />
                <CreateButton
                    v-if="userStore.can('update_platform')"
                    aria-controls="platform-disclaimers-modals"
                    @click.stop="() => handleEditOrCreatePlatformDisclaimer()"
                />
            </template>
        </PlatformDisclaimersTable>
        <PlatformPoliciesTable v-if="platform.platform_uid">
            <template #actions>
                <DeleteBulkButton
                    v-if="userStore.can('update_platform')"
                    v-model:selected="selectedPlatformPoliciesIds"
                    @click.stop="() => handleDeletePlatformPolicies()"
                />
                <CreateButton
                    v-if="userStore.can('update_platform')"
                    aria-controls="platform-policy-modals"
                    @click.stop="() => handleEditOrCreatePlatformPolicy()"
                />
            </template>
        </PlatformPoliciesTable>
        <PlatformCookieConsentServicesTable v-if="platform.platform_uid">
            <template #actions>
                <DeleteBulkButton
                    v-if="userStore.can('manage_cookie_consent_services')"
                    v-model:selected="selectedPlatformCookieConsentServicesIds"
                    @click.stop="
                        () => handleDeletePlatformCookieConsentServices()
                    "
                />
                <LoadDefaultsButton
                    v-if="
                        userStore.can('manage_cookie_consent_services') &&
                        platformCookieConsentServices.length === 0
                    "
                    @click.stop="() => handleLoadDefaultCookieConsentServices()"
                />
                <CreateButton
                    v-if="userStore.can('manage_cookie_consent_services')"
                    aria-controls="platform-cookie-consent-service-modals"
                    @click.stop="() => handleEditOrCreateCookieConsentService()"
                />
            </template>
        </PlatformCookieConsentServicesTable>
        <PlatformAttachmentsTable v-if="platform.platform_uid">
            <template #actions>
                <DeleteBulkButton
                    v-if="userStore.can('update_platform')"
                    v-model:selected="selectedPlatformAttachmentsIds"
                    @click.stop="() => handleDeletePlatformAttachments()"
                />
                <CreateButton
                    v-if="userStore.can('update_platform')"
                    aria-controls="platform-attachment-modals"
                    @click.stop="() => handleUpdateOrCreatePlatformAttachment()"
                />
            </template>
        </PlatformAttachmentsTable>
        <PlatformDocumentsTable v-if="platform.platform_uid">
            <template #actions>
                <DeleteBulkButton
                    v-if="userStore.can('update_platform')"
                    v-model:selected="selectedPlatformDocumentsIds"
                    @click.stop="() => handleDeletePlatformDocuments()"
                />
                <CreateButton
                    v-if="userStore.can('update_platform')"
                    aria-controls="platform-document-modals"
                    @click.stop="() => handleUpdateOrCreatePlatformDocument()"
                />
            </template>
        </PlatformDocumentsTable>
        <PlatformWebhooksTable v-if="platform.webhooks_activated">
            <template #actions>
                <DeleteBulkButton
                    v-if="userStore.can('update_platform')"
                    v-model:selected="selectedPlatformWebhooksIds"
                    @click.stop="() => handleDeletePlatformWebhooks()"
                />
                <CreateButton
                    v-if="userStore.can('update_platform')"
                    aria-controls="platform-webhooks-modals"
                    @click.stop="() => handleEditOrCreatePlatformWebhook()"
                />
            </template>
        </PlatformWebhooksTable>
        <PlatformTaxDocumentsTable v-if="platform.platform_uid">
            <template #actions>
                <CreateButton
                    v-if="userStore.can('update_platform')"
                    aria-controls="platform-tax-documents-modals"
                    @click.stop="() => handleEditOrCreatePlatformTaxDocument()"
                />
                <DeleteBulkButton
                    v-if="userStore.can('update_platform')"
                    v-model:selected="selectedPlatformTaxDocumentsIds"
                    @click.stop="() => handleDeletePlatformTaxDocuments()"
                />
            </template>
        </PlatformTaxDocumentsTable>
        <PlatformMembershipDocumentsTable v-if="platform.platform_uid">
            <template #actions>
                <CreateButton
                    v-if="userStore.can('update_platform')"
                    aria-controls="platform-membership-documents-modals"
                    @click.stop="
                        () => handleUpdateOrCreatePlatformMembershipDocument()
                    "
                />
                <DeleteBulkButton
                    v-if="userStore.can('update_platform')"
                    v-model:selected="selectedPlatformMembershipDocumentsIds"
                    @click.stop="
                        () => handleDeletePlatformMembershipDocuments()
                    "
                />
            </template>
        </PlatformMembershipDocumentsTable>
        <PlatformAcceptanceDocumentsTable v-if="platform.platform_uid">
            <template #actions>
                <CreateButton
                    v-if="userStore.can('update_platform')"
                    aria-controls="platform-acceptance-documents-modals"
                    @click.stop="
                        () => handleUpdateOrCreatePlatformAcceptanceDocument()
                    "
                />
                <DeleteBulkButton
                    v-if="userStore.can('update_platform')"
                    v-model:selected="selectedPlatformAcceptanceDocumentsIds"
                    @click.stop="
                        () => handleDeletePlatformAcceptanceDocuments()
                    "
                />
            </template>
        </PlatformAcceptanceDocumentsTable>
        <PlatformSelfDisclosureFormsTable v-if="platform.platform_uid">
            <template #actions>
                <CreateButton
                    v-if="userStore.can('update_platform')"
                    aria-controls="platform-self-disclosure-forms-modals"
                    @click.stop="() => handleUpdateOrCreateSelfDisclosureForm()"
                />
                <DeleteBulkButton
                    v-if="userStore.can('update_platform')"
                    v-model:selected="selectedSelfDisclosureFormsIds"
                    @click.stop="() => handleDeleteSelfDisclosureForms()"
                />
            </template>
        </PlatformSelfDisclosureFormsTable>
        <AssetsTable
            v-if="platform.platform_name"
            :initial-filters="{
                platform_name_exact: platform.platform_name,
            }"
            :zoho-sync="platform.zoho_sync_enabled"
            :create-initial-values="assetTableCreateInitialValues"
            :create-disabled-fields="['platform_name']"
        />
        <CampaignsTable
            v-if="platform.platform_name"
            :initial-filters="{
                platform_name_exact: platform.platform_name,
            }"
            :create-initial-values="campaignsTableCreateInitialValues"
            :create-disabled-fields="['platform_name']"
        />
        <OrdersTable
            v-if="platform.platform_name"
            :zoho-sync="platform.zoho_sync_enabled"
            :initial-filters="{
                platform_name_exact: platform.platform_name,
            }"
        />
        <AccountsTable
            v-if="platform.platform_name"
            :zoho-sync="platform.zoho_sync_enabled"
            :initial-filters="{
                platform_name_exact: platform.platform_name,
            }"
        />
        <OrderCertificateTable
            v-if="platform.certificate_active"
            :platform="platform"
        />
        <UserTrackingTable
            v-if="
                platform.platform_uid &&
                userStore.can('view_user_tracking_history')
            "
            :initial-filters="{
                platform_name: platform.platform_name,
            }"
        />
        <AuditsTable
            v-if="platform.platform_name"
            :initial-filters="{
                auditable_id: platform.platform_uid,
                auditable_type: auditedModels.platform.value,
            }"
        />
        <OrderBooksTable
            v-if="platform.platform_name"
            :initial-filters="{
                platform_name_exact: platform.platform_name,
            }"
        />
        <ListingsTable
            v-if="platform.platform_name"
            :initial-filters="{
                platform_name_exact: platform.platform_name,
            }"
        />
        <BuyRequestsTable
            v-if="platform.platform_name"
            :initial-filters="{
                platform_name_exact: platform.platform_name,
            }"
        />
        <OffersTable
            v-if="platform.platform_name"
            :initial-filters="{
                platform_name_exact: platform.platform_name,
            }"
        />
        <template #modals>
            <DeleteItemModal
                id="delete-platform-modal"
                v-model="platform"
                v-model:open="deletePlatformModalOpen"
                title="modal_title_delete_platform"
                name-key="platform_name"
                id-key="platform_uid"
                @submit="deletePlatform"
            />
        </template>
        <ModalWrapper v-if="isModalOpen" />
    </PageWrapper>
</template>
<script setup lang="ts">
import {
    ModalButtonStyles,
    useActionsMenu,
    useModal,
} from '@composables/common'
import { usePlatformAttachmentsTable } from '@composables/platforms/usePlatformAttachmentsTable'
import { usePlatformCookieConsentServicesTable } from '@composables/platforms/usePlatformCookieConsentServicesTable'
import { usePlatformDisclaimersTable } from '@composables/platforms/usePlatformDisclaimersTable'
import { usePlatformDocumentsTable } from '@composables/platforms/usePlatformDocumentsTable'
import { usePlatformEmailTemplatesTable } from '@composables/platforms/usePlatformEmailTemplatesTable'
import { usePlatformPoliciesTable } from '@composables/platforms/usePlatformPoliciesTable'
import { usePlatformWebhooksTable } from '@composables/platforms/usePlatformWebhooksTable'
import BackButton from '@partials/actions/BackButton.vue'
import CreateButton from '@partials/actions/CreateButton.vue'
import DeleteBulkButton from '@partials/actions/DeleteBulkButton.vue'
import DeleteButton from '@partials/actions/DeleteButton.vue'
import EditButton from '@partials/actions/EditButton.vue'
import LoadDefaultsButton from '@partials/actions/LoadDefaultsButton.vue'
import AssetsTable from '@partials/assets/AssetsTable.vue'
import CampaignsTable from '@partials/campaigns/CampaignsTable.vue'
import ExportMembershipDocumentsForm from '@partials/membershipDocuments/ExportMembershipDocumentsForm.vue'
import OrdersTable from '@partials/orders/OrdersTable.vue'
import PageWrapper from '@partials/pages/PageWrapper.vue'
import PlatformTitle from '@partials/platforms/PlatformTitle.vue'
import BuyRequestsTable from '@partials/secondaryMarket/BuyRequestsTable.vue'
import ListingsTable from '@partials/secondaryMarket/ListingsTable.vue'
import OffersTable from '@partials/secondaryMarket/OffersTable.vue'
import OrderBooksTable from '@partials/secondaryMarket/OrderBooksTable.vue'
import { DeleteItemModal } from '@src/components'
import SummaryWrapper from '@src/components/SummaryWrapper.vue'
import { useEnum, useSummary } from '@src/composables/common'
import { usePlatformAcceptanceDocumentsTable } from '@src/composables/platforms/usePlatformAcceptanceDocumentsTable'
import { usePlatformMembershipDocumentsTable } from '@src/composables/platforms/usePlatformMembershipDocumentsTable'
import { usePlatformTaxDocumentsTable } from '@src/composables/platforms/usePlatformTaxDocumentsTable'
import { useSelfDisclosureFormsTable } from '@src/composables/platforms/useSelfDisclosureFormsTable'
import { usePaymentAccountSummary } from '@src/composables/summaries/usePaymentAccountSummary'
import { MembershipDocumentPostStatusType, QRCodeType } from '@src/enums'
import {
    FundingTotalStatus,
    PlatformComplianceProfile,
} from '@src/enums/Platform'
import AccountsTable from '@src/partials/accounts/AccountsTable.vue'
import CustomizePlatformButton from '@src/partials/actions/CustomizePlatformButton.vue'
import AuditsTable from '@src/partials/audits/AuditsTable.vue'
import OrderCertificateTable from '@src/partials/platforms/OrderCertificateTable.vue'
import UserTrackingTable from '@src/partials/tracking/UserTrackingTable.vue'
import {
    Asset,
    Campaign,
    CookieConsentService,
    Platform,
    PlatformAcceptanceDocument,
    PlatformAttachment,
    PlatformDisclaimer,
    PlatformDocument,
    PlatformEmailTemplate,
    PlatformMembershipDocument,
    PlatformPolicy,
    PlatformTaxDocument,
    PlatformWebhook,
    SelfDisclosureForm,
} from '@src/types'
import { SummaryItem } from '@src/types/Summary'
import { getCookieConsentServices } from '@src/utils/api/cookieConsentServices'
import {
    deletePlatform as apiDeletePlatform,
    apiExportOrderTransferHistory,
    apiExportPlatformRequestedMembershipDocumentsReport,
    getPlatform,
    updatePlatform,
} from '@src/utils/api/platforms'
import { useAuditsStore } from '@src/utils/stores/audits'
import { useUserStore } from '@src/utils/stores/user'
import { useToastStore } from '@stores/toast'
import moment from 'moment'
import { storeToRefs } from 'pinia'
import { computed, h, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

const router = useRouter()
const route = useRoute()
const toast = useToastStore()
const i18n = useI18n()
const userStore = useUserStore()
const { getEnumLabel } = useEnum()
const auditsStore = useAuditsStore()

const platform = ref<Platform>(new Platform())
const deletePlatformModalOpen = ref(false)

const platformAttachments = ref<PlatformAttachment[]>([])
const platformWebhooks = ref<PlatformWebhook[]>([])
const platformPolicies = ref<PlatformPolicy[]>([])
const platformEmailTemplates = ref<PlatformEmailTemplate[]>([])
const platformDisclaimers = ref<PlatformDisclaimer[]>([])
const platformDocuments = ref<PlatformDocument[]>([])
const platformTaxDocuments = ref<PlatformTaxDocument[]>([])
const platformMembershipDocuments = ref<PlatformMembershipDocument[]>([])
const platformAcceptanceDocuments = ref<PlatformAcceptanceDocument[]>([])
const platformSelfDisclosureForms = ref<SelfDisclosureForm[]>([])
const platformCookieConsentServices = ref<CookieConsentService[]>([])

const { ModalWrapper, openModal } = useModal()

const { auditedModels } = storeToRefs(auditsStore)

const isModalOpen = ref(false)

const { PaymentAccountSummary } = usePaymentAccountSummary(
    computed(() => platform.value?.payment_account ?? null)
)

const { ActionsMenu } = useActionsMenu(
    {
        id: 'platform-actions-menu',
        align: 'right',
    },
    [
        {
            label: 'export_membership_documents',
            icon: 'fa-sack-dollar',
            color: 'text-primary-accent-600',
            action: handleExportMembershipDocuments,
            condition: () => userStore.can('update_platform'),
        },
        {
            label: 'export_order_transfer_history',
            icon: 'fa-file-import',
            color: 'text-primary-accent-600',
            action: exportOrderTransferHistory,
        },
    ]
)

const platformDetailsItems = computed<SummaryItem[]>(() => {
    return [
        {
            label: i18n.t('id'),
            value: platform.value.platform_uid,
            copy: true,
        },
        {
            label: i18n.t('platform_name'),
            value: platform.value.platform_name,
            copy: true,
        },
        {
            label: i18n.t('parent_platform_name'),
            value: platform.value.parent_platform_name,
            condition: !!platform.value.parent_platform_name,
            special: 'link',
            linkURL: `/platforms/${platform.value.parent_platform_name}`,
            copy: true,
        },
        {
            label: i18n.t('clearing_systems'),
            value: platform.value.clearing_systems.length
                ? platform.value.clearing_systems
                      .map((clearing_system) => {
                          return clearing_system.clearing_system
                      })
                      .join(', ')
                : '-',
        },
        {
            label: i18n.t('url'),
            value: platform.value.platform_url,
            special: 'link',
            icon: 'fa-chain',
            copy: true,
        },
        {
            label: i18n.t('terms'),
            value: platform.value.terms_url,
            special: 'link',
            icon: 'fa-chain',
            copy: true,
        },
        {
            label: i18n.t('privacy_policy'),
            value: platform.value.privacy_url,
            special: 'link',
            icon: 'fa-chain',
            copy: true,
        },
        {
            label: i18n.t('qr_code_type'),
            value: getEnumLabel(
                QRCodeType,
                platform.value.payment_qr_code_type
            ),
        },
        {
            label: i18n.t('is_realm'),
            value: platform.value.is_realm,
            special: 'check',
            condition: userStore.is('developer'),
        },
        {
            label: i18n.t('is_platform'),
            value: platform.value.is_platform,
            special: 'check',
            condition: userStore.is('developer'),
        },
        {
            label: i18n.t('platform_logo'),
            value: platform.value.platform_logo?.url ?? null, // Ensure it falls back to null if undefined
            special: 'image',
        },
        {
            label: i18n.t('created_by'),
            value: platform.value.created_by,
            copy: true,
            condition: platform.value.created_by !== null,
        },
        {
            label: i18n.t('funding_total_status'),
            value: platform.value.funding_total_status
                .map((item) => i18n.t(FundingTotalStatus[item]))
                .join(', '),
        },
        {
            label: i18n.t('webhooks_activated'),
            value: platform.value.webhooks_activated,
            special: 'check',
        },
        {
            label: i18n.t('voucher_wallet'),
            value: platform.value.voucher_wallet,
            condition: platform.value.voucher_wallet !== null,
        },
        {
            label: i18n.t('has_credential_simply_tokenized'),
            value: platform.value.has_credential_simply_tokenized,
            special: 'check',
        },
        {
            label: i18n.t('has_credential_sproof'),
            value: platform.value.has_credential_sproof,
            special: 'check',
        },
        {
            label: i18n.t('has_credential_voucherify'),
            value: platform.value.has_credential_voucherify,
            special: 'check',
        },
        {
            label: i18n.t('esac_kyc_active'),
            value: platform.value.esac_kyc_active,
            special: 'check',
            condition: userStore.can('view_esac_kyc_active'),
        },
        {
            label: i18n.t('acceptance_document_active'),
            value: platform.value.acceptance_document_active,
            special: 'check',
        },
        {
            label: i18n.t('compliance_profile'),
            value: getEnumLabel(
                PlatformComplianceProfile,
                platform.value.compliance_profile
            ),
            condition: platform.value.compliance_profile !== null,
        },
        {
            label: i18n.t('transfer_investment'),
            value: platform.value.order_transfer_enabled,
            special: 'check',
        },
        {
            label: i18n.t('require_kyc_id_for_investments'),
            value: platform.value.require_kyc_id_for_investments,
            special: 'check',
        },
        {
            label: i18n.t('require_bank_account_for_investments'),
            value: platform.value.require_bank_account_for_investments,
            special: 'check',
        },
        {
            label: i18n.t('require_kyc_address_for_investments'),
            value: platform.value.require_kyc_address_for_investments,
            special: 'check',
        },
        {
            label: i18n.t('require_kyc_tax_for_investments'),
            value: platform.value.require_kyc_tax_for_investments,
            special: 'check',
        },
        {
            label: i18n.t('redirect_to_another_platform'),
            value: platform.value.redirect_to_another_platform,
            special: 'check',
        },
        {
            label: i18n.t('redirection_url'),
            value: platform.value.redirection_url,
            condition: platform.value.redirect_to_another_platform,
        },

        {
            label: i18n.t('redirect_on_login'),
            value: platform.value.redirect_on_login,
            special: 'check',
        },

        {
            label: i18n.t('redirect_on_register'),
            value: platform.value.redirect_on_register,
            special: 'check',
        },
        {
            label: i18n.t('created_at'),
            value: moment(platform.value.created_on).format('DD.MM.yyyy HH:mm'),
        },
        {
            label: i18n.t('updated_at'),
            value: moment(platform.value.updated_at).format('DD.MM.yyyy HH:mm'),
        },
        {
            label: i18n.t('sm_marketplace'),
            value: platform.value.sm_marketplace,
            special: 'check',
        },
    ]
})

const assetTableCreateInitialValues = computed(() => {
    return {
        ...new Asset(),
        ...{
            platform_name: platform.value.platform_name,
        },
    }
})

const campaignsTableCreateInitialValues = computed(() => {
    return {
        ...new Campaign(),
        ...{
            platform_name: platform.value.platform_name,
        },
    }
})

const { Summary: PlatformSummary } = useSummary(platformDetailsItems)

const {
    PlatformAttachmentsTable,
    handleUpdateOrCreatePlatformAttachment,
    selectedPlatformAttachmentsIds,
    handleDeletePlatformAttachments,
} = usePlatformAttachmentsTable(
    platformAttachments,
    update,
    openModal,
    isModalOpen
)

const {
    PlatformWebhooksTable,
    selectedPlatformWebhooksIds,
    handleEditOrCreatePlatformWebhook,
    handleDeletePlatformWebhooks,
} = usePlatformWebhooksTable(platformWebhooks, update, openModal, isModalOpen)

const {
    PlatformPoliciesTable,
    selectedPlatformPoliciesIds,
    handleDeletePlatformPolicies,
    handleEditOrCreatePlatformPolicy,
} = usePlatformPoliciesTable(platformPolicies, update, openModal, isModalOpen)

const {
    PlatformCookieConsentServicesTable,
    selectedPlatformCookieConsentServicesIds,
    handleDeletePlatformCookieConsentServices,
    handleEditOrCreateCookieConsentService,
    handleLoadDefaultCookieConsentServices,
} = usePlatformCookieConsentServicesTable(
    computed(() => platform.value.platform_name),
    platformCookieConsentServices,
    openModal,
    isModalOpen
)

const {
    PlatformEmailTemplatesTable,
    selectedPlatformEmailTemplatesIds,
    handleEditOrCreatePlatformEmailTemplate,
    handleDeletePlatformEmailTemplates,
} = usePlatformEmailTemplatesTable(
    platformEmailTemplates,
    platform,
    update,
    openModal,
    isModalOpen
)

const {
    PlatformDisclaimersTable,
    selectedPlatformDisclaimersIds,
    handleEditOrCreatePlatformDisclaimer,
    handleDeletePlatformDisclaimers,
} = usePlatformDisclaimersTable(
    platformDisclaimers,
    update,
    openModal,
    isModalOpen
)

const {
    PlatformDocumentsTable,
    selectedPlatformDocumentsIds,
    handleUpdateOrCreatePlatformDocument,
    handleDeletePlatformDocuments,
} = usePlatformDocumentsTable(platformDocuments, update, openModal, isModalOpen)

const {
    PlatformTaxDocumentsTable,
    handleEditOrCreatePlatformTaxDocument,
    handleDeletePlatformTaxDocuments,
    selectedPlatformTaxDocumentsIds,
} = usePlatformTaxDocumentsTable(
    platformTaxDocuments,
    update,
    openModal,
    isModalOpen
)

const {
    PlatformMembershipDocumentsTable,
    handleUpdateOrCreatePlatformMembershipDocument,
    handleDeletePlatformMembershipDocuments,
    selectedPlatformMembershipDocumentsIds,
} = usePlatformMembershipDocumentsTable(
    platformMembershipDocuments,
    update,
    openModal,
    isModalOpen
)

const {
    PlatformAcceptanceDocumentsTable,
    handleUpdateOrCreatePlatformAcceptanceDocument,
    handleDeletePlatformAcceptanceDocuments,
    selectedPlatformAcceptanceDocumentsIds,
} = usePlatformAcceptanceDocumentsTable(
    platformAcceptanceDocuments,
    update,
    openModal,
    isModalOpen
)

const {
    PlatformSelfDisclosureFormsTable,
    handleUpdateOrCreateSelfDisclosureForm,
    handleDeleteSelfDisclosureForms,
    selectedSelfDisclosureFormsIds,
} = useSelfDisclosureFormsTable(
    platformSelfDisclosureForms,
    update,
    openModal,
    isModalOpen
)

const membershipDocumentPostStatus = ref(
    MembershipDocumentPostStatusType.REQUESTED
)

function openDeletePlatformModal() {
    deletePlatformModalOpen.value = true
}

async function deletePlatform() {
    if (!platform.value?.platform_name) return
    await apiDeletePlatform(platform.value?.platform_name)
    toast.success(
        i18n.t('toast_success_platform_deleted', {
            platform: platform.value?.platform_name,
        })
    )
    router.push({ name: 'platforms' })
}

function editPlatform() {
    router.push({
        name: 'platform.edit',
        params: { slug: platform.value?.platform_name },
    })
}

function customizePlatform() {
    router.push({
        name: 'platform.customize',
        params: { platform: platform.value?.platform_name },
    })
}

function toPlatforms() {
    router.push({ name: 'platforms' })
}

function handleExportMembershipDocuments() {
    isModalOpen.value = true

    openModal(
        {
            id: 'export_membership_documents_modal',
            title: 'export_membership_documents',
            open: isModalOpen,
            width: '2xl',
            onCancel: () => (isModalOpen.value = false),
            onSubmit: () => exportMembershipDocumentsReport(),
        },
        () =>
            // @ts-expect-error Type mismatch between render helper and SFC props
            h(ExportMembershipDocumentsForm, {
                modelValue: membershipDocumentPostStatus,
            }),
        {
            submitButtonText: 'export',
            styles: {
                submitButton: ModalButtonStyles['BLUE'],
            },
        }
    )
}

async function load() {
    const slug = route.params.slug as string
    const platformRequest = await getPlatform(slug)
    platform.value = platformRequest.data.platform
    platformAttachments.value = platform.value.attachments
    platformWebhooks.value = platform.value.webhooks
    platformPolicies.value = platform.value.policies
    platformEmailTemplates.value = platform.value.email_templates
    platformDisclaimers.value = platform.value.disclaimers
    platformDocuments.value = platform.value.documents
    platformTaxDocuments.value = platform.value.tax_documents
    platformMembershipDocuments.value = platform.value.membership_documents
    platformAcceptanceDocuments.value = platform.value.acceptance_documents
    platformSelfDisclosureForms.value = platform.value.self_disclosure_forms

    if (platform.value.platform_name) {
        const cookieConsentServicesResponse = await getCookieConsentServices(
            platform.value.platform_name
        )
        platformCookieConsentServices.value =
            cookieConsentServicesResponse.data.cookie_consent_services
    }
}

async function update(successMessage: string | null = null) {
    await updatePlatform(platform.value)
    if (successMessage) {
        toast.success(successMessage)
    }
    await load()
}

async function exportMembershipDocumentsReport() {
    await apiExportPlatformRequestedMembershipDocumentsReport(
        platform.value,
        membershipDocumentPostStatus.value
    )
        .catch((error) => {
            throw error
        })
        .then(() => {
            toast.success(i18n.t('toast_success_export_csv'))
            isModalOpen.value = false
        })
}

async function exportOrderTransferHistory() {
    await apiExportOrderTransferHistory(platform.value)
        .catch((error) => {
            throw error
        })
        .then(() => {
            toast.success(i18n.t('toast_success_export_csv'))
        })
}

watch(
    () => route.params.slug,
    async () => {
        await load()
    }
)

onMounted(async () => {
    await load()
})
</script>
<style scoped lang="sass">
.details
    @apply flex flex-col gap-4 grow
    .detail
        .label
            @apply font-bold mr-2
            &:after
                content: ':'
        .value
            a
                @apply text-blue-500
</style>
